import * as React from "react";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import SearchBar from "./SearchBar";
import {
  Paper,
  Stack,
  Table,
  styled,
  Typography,
  Chip,
  Box,
  Skeleton,
  LinearProgress,
  Button,
  InputBase,
} from "@mui/material";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";

import { DndContext, closestCenter } from "@dnd-kit/core";
import { useSortableTable } from "../../contexts/hooks/useSortableTable";

import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import { usePreload } from "../../contexts/DataContext/PreloadContext";

export default function TabulatedGridCollapsibleV2({
  headers,
  data,
  search,
  pagination,
  rowActions,
  height,
  isDraggable = false,
  onReorder,
  variant = "default",
  loading = false,
  emptyState,
  emptyPlaceholder = "—",
}) {
  const modern = variant === "modern";
  const cellPadding = modern ? { px: 2, py: 1.25 } : { p: 0.5 };
  const colCount = headers.length + 1;
  const paginationSizeOptions = !pagination?.pageSizeOption
    ? [25, 50, 100]
    : pagination?.pageSizeOption;

  const { openImageDialog } = usePreload();

  const { rows, handleDragEnd } = useSortableTable({
    data,
    isDraggable,
    onReorder,
  });

  const tableContent = (
    <TableBody>
      {modern && loading && !rows?.length
        ? Array.from({ length: 8 }).map((_, r) => (
            <TableRow key={`skeleton-${r}`}>
              {Array.from({ length: colCount }).map((__, c) => (
                <TableCell key={c} sx={cellPadding}>
                  {c < colCount - 1 && <Skeleton variant="text" />}
                </TableCell>
              ))}
            </TableRow>
          ))
        : null}
      {modern && !loading && !rows?.length ? (
        <TableRow>
          <TableCell colSpan={colCount} sx={{ border: 0 }}>
            {emptyState ?? (
              <Stack
                alignItems="center"
                spacing={1}
                sx={{ py: 8, color: "text.secondary" }}
              >
                <SearchOffRoundedIcon sx={{ fontSize: 40 }} />
                <Typography>No results found</Typography>
              </Stack>
            )}
          </TableCell>
        </TableRow>
      ) : null}
      {rows?.map((row, rindex) => {
        return (
          <SortableRow
            key={row.id ?? rindex}
            row={row}
            isDraggable={isDraggable}
            modern={modern}
          >
            {headers.map((h, i) => {
              let allStatus = [];

              if (Array.isArray(h.prop) && h.isStatus) {
                allStatus = h.prop.map((m) => row[m] ?? true);
              }

              const isAllStatusActive = allStatus?.every(
                (status) => status === true,
              );

              let value = String(row[h.prop] ?? "");

              return (
                // keyed by column only: the row key already identifies the row,
                // and an index here would remount cell state when rows move
                <React.Fragment key={`col-${i}`}>
                  <TableCell
                    sx={cellPadding}
                    style={{ verticalAlign: "middle" }}
                    align={h.align}
                  >
                    {h?.isSelectabale && row?.featureSelectable ? (
                      <Chip color="info" label="Yes" size="small" />
                    ) : h?.isSelectabale && !row?.featureSelectable ? (
                      <Chip color="warning" label="No" size="small" />
                    ) : h.isIndex ? (
                      <span>{rindex + 1}</span>
                    ) : h.isImage && modern && !value ? (
                      <Box component="span" sx={{ color: "text.disabled" }}>
                        {emptyPlaceholder}
                      </Box>
                    ) : h.isImage ? (
                      <Box onPointerDown={(e) => e.stopPropagation()}>
                        <img
                          srcSet={``}
                          width={24}
                          height={value ? 24 : undefined}
                          src={`${value ?? ""}`}
                          alt={`${value ?? ""}`}
                          loading="lazy"
                          style={{ cursor: "pointer" }}
                          onClick={() => openImageDialog(value)}
                        />
                      </Box>
                    ) : h.format ? (
                      h.format(value)
                    ) : h.link && modern && !value ? (
                      <Box component="span" sx={{ color: "text.disabled" }}>
                        {emptyPlaceholder}
                      </Box>
                    ) : h.link ? (
                      <Box onPointerDown={(e) => e.stopPropagation()}>
                        <Box
                          component="span"
                          onClick={() => h.link.action(row)}
                          {...(modern && {
                            role: "link",
                            tabIndex: 0,
                            onKeyDown: (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                h.link.action(row);
                              }
                            },
                          })}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.3,
                            cursor: "pointer",
                            ...(modern
                              ? {
                                  color: "primary.main",
                                  fontWeight: 600,
                                  fontVariantNumeric: "tabular-nums",
                                  textDecoration: "none",
                                  borderRadius: 0.5,
                                  "&:hover": { textDecoration: "underline" },
                                  "&:focus-visible": {
                                    outline: "2px solid",
                                    outlineColor: "primary.main",
                                    outlineOffset: 2,
                                  },
                                }
                              : {
                                  color: "#0d6efd",
                                  textDecoration: "underline",
                                  "&:hover": {
                                    color: "primary.dark",
                                  },
                                }),
                          }}
                        >
                          <span>{value}</span>

                          {h.withIsNewStatus && row.isSelectionNew && (
                            <Chip color="success" label="new" size="small" />
                          )}
                        </Box>
                      </Box>
                    ) : h.isStatus ? (
                      <Chip
                        label={
                          row.isSelectionNew
                            ? "New"
                            : isAllStatusActive
                              ? "Active"
                              : "Inactive"
                        }
                        color={
                          row.isSelectionNew
                            ? "success"
                            : isAllStatusActive
                              ? "info"
                              : "warning"
                        }
                        size="small"
                      />
                    ) : h.isUnit ? (
                      row.unit?.[0]?.unitAbbreviation ||
                      row.unit?.[0]?.unitDisplayName ||
                      ""
                    ) : h.isValueCodes ? (
                      <DisplayTableValues
                        showValues={h.showValues}
                        values={row.values}
                        includedFeatureValues={row.includedFeatureValues}
                      />
                    ) : (
                      <Box display={"flex"} gap={0.3}>
                        {modern && !value ? (
                          <Box component="span" sx={{ color: "text.disabled" }}>
                            {emptyPlaceholder}
                          </Box>
                        ) : (
                          <span>{value ?? ""}</span>
                        )}

                        {h.withIsNewStatus && row.isSelectionNew && (
                          <Chip color="success" label="new" size="small" />
                        )}
                      </Box>
                    )}
                  </TableCell>
                </React.Fragment>
              );
            })}

            <TableCell
              sx={{
                ...cellPadding,
                ...(modern && { position: "sticky", right: 0, zIndex: 1 }),
              }}
              className={modern ? "row-actions-cell" : undefined}
              align="right"
            >
              <Box onPointerDown={(e) => e.stopPropagation()}>
                <Stack>
                  <Stack direction="row" spacing={1} className="row-actions">
                    {typeof rowActions === "function"
                      ? rowActions(row)
                      : rowActions}
                  </Stack>
                </Stack>
              </Box>
            </TableCell>
          </SortableRow>
        );
      })}
    </TableBody>
  );

  const tableHeader = (
    <TableHead
      sx={
        modern
          ? {
              "& .MuiTableCell-head": {
                // opaque: action.hover is translucent and rows show through a sticky header
                bgcolor: (theme) => theme.palette.background.paper,
                backgroundImage: (theme) =>
                  `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
                color: "text.secondary",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                borderBottom: 1,
                borderColor: "divider",
              },
            }
          : undefined
      }
    >
      <TableRow>
        {headers.map((h, i) => (
          <React.Fragment key={`head-${h.prop}-${i}`}>
            <TableCell
              align={h.align}
              width={h.width}
              style={{
                minWidth: h.minWidth,
              }}
              sx={cellPadding}
            >
              {modern ? h.text : <strong>{h.text}</strong>}
            </TableCell>
          </React.Fragment>
        ))}

        <TableCell
          sx={{
            ...cellPadding,
            ...(modern && { right: 0, zIndex: 3 }),
          }}
          width={1}
          align="right"
          style={{ minWidth: 30 }}
        >
          {modern ? (
            <Box
              component="span"
              sx={{
                position: "absolute",
                width: "1px",
                height: "1px",
                overflow: "hidden",
                clip: "rect(0 0 0 0)",
                whiteSpace: "nowrap",
              }}
            >
              Actions
            </Box>
          ) : (
            <strong></strong>
          )}
        </TableCell>
      </TableRow>
    </TableHead>
  );

  const modernSearchBar = modern && search && (
    <SearchBar
      variant="modern"
      activeTerm={search?.activeTerm}
      onClear={search?.onClear}
      total={pagination?.totalRows}
      placeHolder={search?.placeHolder}
      handleSearchClick={search?.handleSearchClick}
      onKeyDown={search?.onKeyDown}
      searchType={search?.searchType}
      setSearchType={search?.setSearchType}
      searchOption={search?.searchOption}
    />
  );

  return (
    <>
      {!modern && search && (
        <SearchBar
          sx={{ width: "10%" }}
          placeHolder={search?.placeHolder}
          handleSearchClick={search?.handleSearchClick}
          onKeyDown={search?.onKeyDown}
          searchType={search?.searchType}
          setSearchType={search?.setSearchType}
          searchOption={search?.searchOption}
        />
      )}

      <Paper
        variant={modern ? "outlined" : "elevation"}
        sx={{
          marginTop: modern ? 0 : 2,
          width: "100%",
          overflow: "hidden",
          ...(modern && { borderRadius: 2 }),
        }}
      >
        {modernSearchBar}
        {modern && (
          <LinearProgress
            aria-label="Loading"
            sx={{
              height: 2,
              visibility: loading && rows?.length ? "visible" : "hidden",
            }}
          />
        )}
        <TableContainer
          sx={{
            ...(height && { maxHeight: height }),
          }}
        >
          {isDraggable ? (
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <Table stickyHeader aria-label="sticky table">
                {tableHeader}

                <SortableContext
                  items={rows.map((r) => r.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {tableContent}
                </SortableContext>
              </Table>
            </DndContext>
          ) : (
            <Table stickyHeader aria-label="sticky table">
              {tableHeader}
              {tableContent}
            </Table>
          )}
        </TableContainer>

        {pagination && (
          <TablePagination
            rowsPerPageOptions={paginationSizeOptions}
            component="div"
            count={pagination?.totalRows ?? 0}
            page={pagination?.page}
            rowsPerPage={pagination?.rowsPerPage}
            onPageChange={pagination?.handleChangePage}
            onRowsPerPageChange={pagination?.handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} of ${count}`
            }
            sx={{
              ...(modern && {
                borderTop: 1,
                borderColor: "divider",
              }),
              "& .MuiTablePagination-toolbar": {
                backgroundColor: (theme) =>
                  modern ? "transparent" : theme.palette.grey[200],
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              },
              "& .MuiTablePagination-selectLabel": {
                marginRight: 1,
                marginTop: 2,
              },
              "& .MuiTablePagination-displayedRows": {
                marginLeft: 1,
                marginTop: 2,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  color: (theme) =>
                    modern
                      ? theme.palette.text.secondary
                      : theme.palette.common.black,
                  fontWeight: modern ? 500 : 600,
                },
              "& .MuiTablePagination-actions button": {
                color: (theme) =>
                  modern
                    ? theme.palette.text.secondary
                    : theme.palette.common.black,
              },
            }}
          />
        )}
      </Paper>
    </>
  );
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },

  "&:last-child td, &:last-child th": {
    border: 0,
  },

  "& .row-action-btn": {
    opacity: 0,
    transition: "opacity 0.2s ease",
  },

  "&:hover .row-action-btn": {
    opacity: 1,
  },
}));

const ModernTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },

  "& td, & th": {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  "&:last-child td, &:last-child th": {
    border: 0,
  },

  // pinned to the right so wide tables never scroll the menu out of view;
  // opaque so cells scrolling underneath don't show through
  "& .row-actions-cell": {
    backgroundColor: theme.palette.background.paper,
    boxShadow: `inset 1px 0 0 ${theme.palette.divider}`,
  },

  "&:hover .row-actions-cell": {
    backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
  },

  "& .row-action-btn": {
    color: theme.palette.text.secondary,
  },

  "&:hover .row-action-btn, & .row-action-btn:focus-visible": {
    color: theme.palette.text.primary,
  },
}));

const DisplayTableValues = ({ includedFeatureValues, values, showValues }) => {
  // `values` can hold the same code twice (feature defaults + included values),
  // so walk the included codes in order and take the first match for each
  const orderedValues = React.useMemo(() => {
    const byCode = new Map();
    for (const v of values ?? []) {
      if (!byCode.has(v.valueCode)) byCode.set(v.valueCode, v);
    }
    const codes = [...new Set(includedFeatureValues?.split("|") ?? [])];
    return codes
      .filter((code) => byCode.has(code))
      .map((code, i) => ({ ...byCode.get(code), position: i + 1 }));
  }, [values, includedFeatureValues]);

  const [expanded, setExpanded] = React.useState(false);
  const [filter, setFilter] = React.useState("");

  if (!showValues || !orderedValues.length) return null;

  const total = orderedValues.length;
  const hiddenCount = total - VALUE_PREVIEW_COUNT;
  const term = filter.trim().toLowerCase();
  const visible = !expanded
    ? orderedValues.slice(0, VALUE_PREVIEW_COUNT)
    : term
      ? orderedValues.filter(
          (v) =>
            v.valueCode?.toLowerCase().includes(term) ||
            v.valueDisplayName?.toLowerCase().includes(term),
        )
      : orderedValues;

  return (
    <Box onPointerDown={(e) => e.stopPropagation()} sx={{ minWidth: 220 }}>
      {expanded && total > VALUE_FILTER_THRESHOLD && (
        <InputBase
          size="small"
          placeholder={`Filter ${total} values…`}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          inputProps={{ "aria-label": "Filter values" }}
          sx={{
            mb: 0.5,
            px: 1,
            width: "100%",
            fontSize: 13,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        />
      )}
      <Box
        component="ol"
        sx={{
          m: 0,
          p: 0,
          listStyle: "none",
          ...(expanded && {
            maxHeight: 240,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.5,
          }),
        }}
      >
        {visible.map((value) => (
          <Box
            component="li"
            key={value.valueCode}
            sx={{
              display: "flex",
              gap: 1,
              py: 0.25,
              fontSize: 13,
              lineHeight: 1.4,
            }}
          >
            <Box
              component="span"
              sx={{
                color: "text.disabled",
                minWidth: 24,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {value.position}
            </Box>
            <Box
              component="span"
              sx={{
                color: "primary.main",
                fontFamily: "monospace",
                whiteSpace: "nowrap",
              }}
            >
              {value.valueCode}
            </Box>
            <Box
              component="span"
              sx={{
                color: "text.secondary",
                minWidth: 0,
                overflowWrap: "anywhere",
              }}
            >
              {value.valueDisplayName}
            </Box>
          </Box>
        ))}
        {expanded && term && !visible.length && (
          <Box component="li" sx={{ fontSize: 13, color: "text.disabled" }}>
            No matching values
          </Box>
        )}
      </Box>
      {hiddenCount > 0 && (
        <Button
          size="small"
          onClick={() => {
            setExpanded((e) => !e);
            setFilter("");
          }}
          aria-expanded={expanded}
          sx={{ mt: 0.25, px: 0.5, minWidth: 0, textTransform: "none" }}
        >
          {expanded ? "Show less" : `+${hiddenCount} more`}
        </Button>
      )}
    </Box>
  );
};

const VALUE_PREVIEW_COUNT = 3;
const VALUE_FILTER_THRESHOLD = 10;

function SortableRow({ row, children, isDraggable, modern }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: row.id,
      disabled: !isDraggable,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDraggable ? "grab" : "default",
  };

  const RowComponent = modern ? ModernTableRow : StyledTableRow;

  return (
    <RowComponent
      key={row.id}
      ref={setNodeRef}
      style={style}
      {...(isDraggable ? attributes : {})}
      {...(isDraggable ? listeners : {})}
    >
      {children}
    </RowComponent>
  );
}
